import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '@/api/client'
import { ApiError } from '@/api/client'
import { Spinner } from '@/components/ui/Spinner'
import { LangSwitcher } from '@/components/ui/LangSwitcher'

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirm) { setError(t('resetPassword.mismatch')); return }
    if (password.length < 8) { setError(t('resetPassword.tooShort')); return }
    setError('')
    setLoading(true)
    try {
      await apiFetch('/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) })
      setDone(true)
      setTimeout(() => navigate('/join'), 3000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card-glow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">❌</div>
          <p className="text-white/60">{t('resetPassword.invalidLink')} <a href="/forgot-password" className="text-emerald-400">{t('resetPassword.requestNew')}</a></p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="absolute top-4 right-4"><LangSwitcher /></div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🔒</div>
          <h1 className="text-2xl font-black text-white">{t('resetPassword.title')}</h1>
        </div>
        <div className="card-glow p-8">
          {done ? (
            <div className="text-center">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-white font-semibold mb-2">{t('resetPassword.successTitle')}</p>
              <p className="text-sm text-white/50">{t('resetPassword.successDesc')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('resetPassword.newPassword')}</label>
                <div className="relative">
                  <input className="input pr-10" type={showPassword ? 'text' : 'password'}
                    placeholder={t('join.passwordPlaceholder')} value={password}
                    onChange={e => { setPassword(e.target.value); setError('') }} autoFocus required />
                  <button type="button" onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-sm">
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1.5 block">{t('resetPassword.confirmPassword')}</label>
                <input className="input" type={showPassword ? 'text' : 'password'}
                  placeholder={t('join.confirmPasswordPlaceholder')} value={confirm}
                  onChange={e => { setConfirm(e.target.value); setError('') }} required />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button type="submit" disabled={loading || !password || !confirm} className="btn-primary mt-1">
                {loading ? <Spinner size="sm" /> : null}
                {t('resetPassword.submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
